import { useState, useEffect } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { useAuth } from "../contexts/AuthContext";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import { LockedFeature } from "@/components/shared/LockedFeature";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Sparkles, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Info,
  Lightbulb, Megaphone, RefreshCcw, Loader2, ArrowRight, BarChart3, Target,
  Minus, Users, DollarSign, ShieldAlert, Zap
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";

const TREND_ICON = { up: TrendingUp, down: TrendingDown, stable: Minus };
const TREND_COLOR = { up: "text-emerald-500", down: "text-red-500", stable: "text-gray-400" };
const ALERT_STYLE = {
  warning: { icon: AlertTriangle, bg: "bg-amber-50 border-amber-200", text: "text-amber-700" },
  success: { icon: CheckCircle, bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700" },
  info: { icon: Info, bg: "bg-blue-50 border-blue-200", text: "text-blue-700" },
};
const PRIORITY_COLOR = { alta: "bg-red-500", media: "bg-amber-500", baja: "bg-blue-500" };
const CAT_ICON = { marketing: Megaphone, operaciones: Target, menu: BarChart3, finanzas: TrendingUp };
const SEG_COLORS = ["#CDA34F", "#10b981", "#f59e0b", "#ef4444", "#6b7280"];

export default function AIInsightsPage() {
  const gate = useFeatureGate("ia_executiva");
  const { authAxios } = useAuth();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInsights = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = forceRefresh ? "/ai/refresh-insights" : "/ai/proactive-insights";
      const method = forceRefresh ? "post" : "get";
      const res = await authAxios[method](endpoint, { timeout: 60000 });
      setInsights(res.data);
      if (forceRefresh) toast.success("Insights atualizados!");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Error generando insights";
      setError(msg);
      if (forceRefresh) toast.error("Erro ao gerar insights");
    }
    setLoading(false);
  };

  useEffect(() => { fetchInsights(false); }, []);

  const healthColor = (score) => {
    if (score >= 80) return "text-emerald-500";
    if (score >= 60) return "text-amber-500";
    return "text-red-500";
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-lg text-xs">
        <p className="font-medium text-gray-600">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="font-bold" style={{ color: p.color }}>{p.name}: {typeof p.value === "number" ? `${p.value.toFixed(2)}€` : p.value}</p>
        ))}
      </div>
    );
  };

  if (gate === "blocked") return <MainLayout><LockedFeature featureName="IA Executiva" addonId="ia_executiva" /></MainLayout>;

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="ai-insights-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#CDA34F] to-[#b8903e] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">IA Ejecutiva</h1>
              <p className="text-sm text-muted-foreground">Insights proactivos, previsiones y análisis inteligente</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => fetchInsights(true)} disabled={loading} data-testid="refresh-insights">
            <RefreshCcw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Analizando..." : "Actualizar"}
          </Button>
        </div>

        {loading && !insights && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-[#CDA34F]/10 flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-[#CDA34F] animate-pulse" />
            </div>
            <p className="text-lg font-semibold">Analizando tu negocio con IA...</p>
            <p className="text-sm text-muted-foreground mt-1">Procesando datos de ventas, clientes y operaciones</p>
            <Loader2 className="w-6 h-6 animate-spin mt-4 text-[#CDA34F]" />
          </div>
        )}

        {error && !insights && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-6 text-center">
              <p className="text-red-600">{error}</p>
              <Button variant="outline" className="mt-3" onClick={fetchInsights}>Reintentar</Button>
            </CardContent>
          </Card>
        )}

        {insights && (
          <>
            {/* Health Score + Forecast Row */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-6 flex items-center gap-6">
                  <div className="relative w-24 h-24 flex-shrink-0">
                    <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                      <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none"
                        stroke={insights.health_score >= 80 ? "#10b981" : insights.health_score >= 60 ? "#f59e0b" : "#ef4444"}
                        strokeWidth="3" strokeDasharray={`${insights.health_score}, 100`} strokeLinecap="round" />
                    </svg>
                    <span className={`absolute inset-0 flex items-center justify-center text-2xl font-bold ${healthColor(insights.health_score)}`}>
                      {insights.health_score}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Salud del Negocio</h2>
                    <p className={`text-lg font-semibold ${healthColor(insights.health_score)}`}>{insights.health_label}</p>
                    <p className="text-sm text-muted-foreground mt-1">Basado en ventas, clientes, tendencias y operaciones</p>
                  </div>
                </CardContent>
              </Card>

              {/* Forecast */}
              {insights.forecast && (
                <Card className="border-[#CDA34F]/20" data-testid="forecast-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-5 h-5 text-[#CDA34F]" />
                      <h3 className="font-bold">Previsión Próxima Semana</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Revenue estimado</p>
                        <p className="text-2xl font-bold text-[#CDA34F]">{insights.forecast.next_week_revenue}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Pedidos estimados</p>
                        <p className="text-2xl font-bold">{insights.forecast.next_week_orders}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      {insights.forecast.trend_direction === "up" && <TrendingUp className="w-4 h-4 text-emerald-500" />}
                      {insights.forecast.trend_direction === "down" && <TrendingDown className="w-4 h-4 text-red-500" />}
                      {insights.forecast.trend_direction === "stable" && <Minus className="w-4 h-4 text-gray-400" />}
                      <span className="text-xs text-muted-foreground">{insights.forecast.reasoning}</span>
                    </div>
                    <Badge variant="outline" className="mt-2 text-[10px]">Confianza: {insights.forecast.confidence}</Badge>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* KPIs */}
            {insights.kpis?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {insights.kpis.map((kpi, i) => {
                  const TIcon = TREND_ICON[kpi.trend] || Minus;
                  return (
                    <Card key={i}>
                      <CardContent className="p-4">
                        <p className="text-xs text-muted-foreground truncate">{kpi.title}</p>
                        <p className="text-xl font-bold mt-1">{kpi.value}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <TIcon className={`w-3 h-3 ${TREND_COLOR[kpi.trend] || ""}`} />
                          <span className={`text-xs ${TREND_COLOR[kpi.trend] || ""}`}>{kpi.change}</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Daily Trend Chart + Customer Segments */}
            <div className="grid md:grid-cols-3 gap-4">
              {/* Trend Chart */}
              {insights.daily_trend?.length > 0 && (
                <Card className="md:col-span-2" data-testid="trend-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#CDA34F]" />Tendencia de Revenue (14 días)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pb-4">
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={insights.daily_trend}>
                        <defs>
                          <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#CDA34F" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#CDA34F" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
                        <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}€`} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#CDA34F" fill="url(#revGrad)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Customer Segments */}
              {insights.customer_segments && (
                <Card data-testid="customer-segments">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#CDA34F]" />Segmentos de Clientes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const segs = insights.customer_segments;
                      const data = [
                        { name: segs.vip?.label || "VIP", value: segs.vip?.count || 0 },
                        { name: segs.regular?.label || "Regular", value: segs.regular?.count || 0 },
                        { name: segs.at_risk?.label || "En riesgo", value: segs.at_risk?.count || 0 },
                        { name: segs.lost?.label || "Perdidos", value: segs.lost?.count || 0 },
                        { name: segs.new?.label || "Nuevos", value: segs.new?.count || 0 },
                      ].filter(d => d.value > 0);
                      const total = data.reduce((s, d) => s + d.value, 0);
                      if (total === 0) return <p className="text-sm text-muted-foreground text-center py-8">Sin datos suficientes</p>;
                      return (
                        <>
                          <ResponsiveContainer width="100%" height={140}>
                            <PieChart>
                              <Pie data={data} innerRadius={35} outerRadius={60} paddingAngle={3} dataKey="value">
                                {data.map((_, i) => <Cell key={i} fill={SEG_COLORS[i % SEG_COLORS.length]} />)}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="space-y-1.5 mt-2">
                            {data.map((d, i) => (
                              <div key={i} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: SEG_COLORS[i % SEG_COLORS.length] }} />
                                  <span className="text-gray-600">{d.name}</span>
                                </div>
                                <span className="font-bold">{d.value}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Money Leaks */}
            {insights.money_leaks?.length > 0 && (
              <div className="space-y-2" data-testid="money-leaks">
                <h3 className="font-semibold text-sm uppercase text-muted-foreground flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-red-500" />Fugas de Dinero Detectadas
                </h3>
                {insights.money_leaks.map((leak, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg border bg-red-50 border-red-200">
                    <DollarSign className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-sm text-red-700">{leak.title}</p>
                        <Badge className="bg-red-600 text-white text-[10px]">{leak.estimated_loss}</Badge>
                      </div>
                      <p className="text-xs text-red-600 mt-0.5">{leak.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Alerts */}
            {insights.alerts?.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold text-sm uppercase text-muted-foreground">Alertas</h3>
                {insights.alerts.map((alert, i) => {
                  const style = ALERT_STYLE[alert.type] || ALERT_STYLE.info;
                  const AIcon = style.icon;
                  return (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${style.bg}`}>
                      <AIcon className={`w-5 h-5 mt-0.5 ${style.text}`} />
                      <div>
                        <p className={`font-semibold text-sm ${style.text}`}>{alert.title}</p>
                        <p className="text-xs text-gray-600 mt-0.5">{alert.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Recommendations */}
            {insights.recommendations?.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-sm uppercase text-muted-foreground flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" />Recomendaciones
                </h3>
                {insights.recommendations.map((rec, i) => {
                  const CIcon = CAT_ICON[rec.category] || Lightbulb;
                  return (
                    <Card key={i}>
                      <CardContent className="p-4 flex items-start gap-3">
                        <div className={`w-2 h-full min-h-[40px] rounded-full ${PRIORITY_COLOR[rec.priority] || "bg-gray-300"}`} />
                        <CIcon className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-sm">{rec.title}</p>
                            <Badge variant="outline" className="text-[10px]">{rec.category}</Badge>
                            {rec.impact && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{rec.impact}</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{rec.description}</p>
                        </div>
                        <Badge className={`${PRIORITY_COLOR[rec.priority]} text-white text-[10px]`}>{rec.priority}</Badge>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Promotion Idea */}
            {insights.promotion_idea && (
              <Card className="border-[#CDA34F]/30 bg-gradient-to-r from-[#CDA34F]/5 to-[#CDA34F]/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-[#CDA34F]" />
                    Idea de Promoción IA
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <h4 className="text-lg font-bold text-gray-900">{insights.promotion_idea.name}</h4>
                  <p className="text-sm text-gray-700 mt-1">{insights.promotion_idea.description}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <Badge className="bg-[#CDA34F] text-white">{insights.promotion_idea.discount}</Badge>
                    <p className="text-xs text-[#CDA34F]">{insights.promotion_idea.reason}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-center">
              <Button variant="outline" onClick={() => window.location.href = "/ai-assistant"} className="gap-2">
                Hacer preguntas a la IA <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}
